'use client'

import { useState, useEffect, useMemo } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { FiUser, FiBell, FiMenu, FiActivity, FiLogOut, FiLayout } from 'react-icons/fi'
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { adminMenuItems, clientHostingMenuItems, miApiMenuItems, erpMenuItems, crmMenuItems } from './Sidebar'
import { cn } from '@/lib/utils'

export default function Navbar() {
  const pathname = usePathname() || ''
  const [usuario, setUsuario] = useState<{ nombreCompleto: string; email: string; isAdmin?: boolean; isHostingUser?: boolean; hostingAccounts?: any[] } | null>(null)
  const [selectedAccount, setSelectedAccount] = useState<any>(null)
  // Estado para las notificaciones
  const [notificaciones, setNotificaciones] = useState<{ conteo: number; alertas: any[] }>({ conteo: 0, alertas: [] })
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({})

  const toggleMenu = (label: string) => {
    setOpenMenus(prev => ({ ...prev, [label]: !prev[label] }))
  }

  useEffect(() => {
    // 1. Fetch Usuario
    fetch('/api/auth/verificar')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setUsuario(data.usuario)
          if (data.usuario.hostingAccounts?.length > 0) {
            const savedAccId = localStorage.getItem('selected_hosting_account')
            const savedAcc = data.usuario.hostingAccounts.find((a: any) => a.id.toString() === savedAccId)
            const initialAcc = savedAcc || data.usuario.hostingAccounts[0]
            setSelectedAccount(initialAcc)
            localStorage.setItem('selected_hosting_account', initialAcc.id.toString())
          }
        }
      })
      .catch(console.error)

    // 2. Notificaciones en Tiempo Real (SSE)
    let eventSource: EventSource | null = null;

    const connectNotificacionesSSE = () => {
      if (eventSource) eventSource.close();
      
      eventSource = new EventSource('/api/notificaciones/events');
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setNotificaciones(data);
        } catch (e) {
          console.error('Error parseando notificaciones SSE:', e);
        }
      };

      eventSource.onerror = (err) => {
        console.error('Error en SSE Notificaciones:', err);
        eventSource?.close();
        setTimeout(connectNotificacionesSSE, 10000); // Reintentar cada 10s
      };
    };

    connectNotificacionesSSE();

    return () => {
      if (eventSource) eventSource.close();
    }
  }, [])

  const marcarComoLeida = async (id: string) => {
    // Solo procesar si el ID viene de la BD (empieza con db-)
    if (id.startsWith('db-')) {
      try {
        const res = await fetch(`/api/notificaciones/${id}`, { method: 'PATCH' })
        const data = await res.json()
        if (data.success) {
          // Actualizar estado local instantáneamente para mejor UX
          setNotificaciones(prev => ({
            ...prev,
            alertas: prev.alertas.map(a => a.id === id ? { ...a, isRead: true } : a),
            conteo: Math.max(0, prev.conteo - 1)
          }))
        }
      } catch (error) {
        console.error('Error al marcar como leída:', error)
      }
    }
  }

  const countBadge = notificaciones.conteo

  const getItems = () => {
    if (!usuario) return []
    const crmPermissions = (usuario as any).crmPermissions || []
    const canViewLeads = crmPermissions.includes('CRM_LEADS_VIEW_ALL') || crmPermissions.includes('CRM_LEADS_VIEW_OWN')
    const canViewTasks = crmPermissions.includes('CRM_TASKS_VIEW_ALL') || crmPermissions.includes('CRM_TASKS_VIEW_OWN')
    const canViewCampaigns = crmPermissions.includes('CRM_CAMPAIGNS_VIEW')
    const canViewTeam = crmPermissions.includes('CRM_TEAM_VIEW') || crmPermissions.includes('CRM_TEAM_MANAGE')
    const canManageConfig = crmPermissions.includes('CRM_CONFIG_VIEW') || crmPermissions.includes('CRM_CONFIG_MANAGE')
    const crmItems = [{
      items: crmMenuItems[0].items.filter((item) => {
        if (item.href === '/empresas' && (usuario as any).crmOnly) return false
        if (item.href === '/crm/leads') return canViewLeads
        if (item.href === '/crm/conversaciones') return canViewLeads
        if (item.href === '/crm/embudo') return canViewLeads
        if (item.href === '/crm/seguimientos') return canViewTasks
        if (item.href === '/crm/campanas') return canViewCampaigns
        if (item.href === '/crm/equipo') return canViewTeam
        if (item.href === '/crm/configuracion') return canManageConfig
        return true
      })
    }]

    if (usuario.isAdmin) {
      if (pathname.startsWith('/erp')) return erpMenuItems
      if (pathname.startsWith('/crm')) return crmItems
      return [{ items: adminMenuItems }]
    }
    if (usuario.isHostingUser) return [{ title: 'MI HOSTING', items: clientHostingMenuItems }]
    
    if (pathname.startsWith('/erp')) return erpMenuItems
    if (pathname.startsWith('/crm')) return crmItems
    return miApiMenuItems
  }

  const items = useMemo(() => getItems(), [usuario, pathname])

  useEffect(() => {
    setOpenMenus((prev) => {
      const next = { ...prev }
      let changed = false

      items.forEach((section: any) => {
        section.items.forEach((item: any) => {
          if (item.subItems?.some((sub: any) => pathname === sub.href) && !next[item.label]) {
            next[item.label] = true
            changed = true
          }
        })
      })

      return changed ? next : prev
    })
  }, [items, pathname])

  return (
    <header className="fixed top-0 right-0 z-30 flex h-16 w-full lg:w-[calc(100%-16rem)] items-center justify-between border-b bg-white/80 px-4 lg:px-6 backdrop-blur-sm">
      <div className="flex items-center gap-4">
        {/* Mobile Menu */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden">
              <FiMenu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0 flex flex-col h-full">
            <div className="flex h-16 items-center border-b px-6">
              <div className="flex items-center gap-2 font-bold text-xl text-primary">
                <FiActivity className="h-6 w-6" />
                <span>{usuario?.isAdmin ? 'MiApi.Cloud' : 'Mi Panel'}</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-4 custom-scrollbar flex flex-col">
              <nav className="space-y-1">
                {items.map((section: any, idx) => (
                  <div key={idx} className="space-y-1">
                    {section.title && (
                      <div className="px-3 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-4">
                        {section.title}
                      </div>
                    )}
                    {section.items.map((item: any) => {
                      const Icon = item.icon
                      const hasSubItems = item.subItems && item.subItems.length > 0
                      const isOpen = openMenus[item.label]
                      const isActive = item.href ? pathname === item.href : item.subItems.some((s: any) => pathname === s.href)
                      
                      if (hasSubItems) {
                        return (
                          <div key={item.label} className="space-y-1">
                            <button
                              onClick={() => toggleMenu(item.label)}
                              className={cn(
                                "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors group",
                                isActive ? "text-primary bg-primary/5" : "text-gray-700 hover:bg-gray-100"
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <Icon className={cn("h-5 w-5", isActive ? "text-primary" : "text-gray-500 group-hover:text-gray-700")} />
                                {item.label}
                              </div>
                              <FiMenu className={cn("h-4 w-4 transition-transform", isOpen ? "rotate-90" : "")} />
                            </button>
                            
                            {isOpen && (
                              <div className="ml-8 space-y-1 border-l pl-3 my-1">
                                {item.subItems.map((sub: any) => (
                                  <Link
                                    key={sub.href}
                                    href={sub.href}
                                    className={cn(
                                      "block rounded-md px-3 py-2 text-xs font-medium transition-colors",
                                      pathname === sub.href ? "text-primary bg-primary/5" : "text-gray-500 hover:text-gray-900"
                                    )}
                                  >
                                    {sub.label}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      }

                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          className={cn(
                            "flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                            isActive 
                              ? "bg-primary text-primary-foreground" 
                              : "text-gray-700 hover:bg-gray-100"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={cn("h-5 w-5", isActive ? "text-primary-foreground" : "text-gray-500")} />
                            {item.label}
                          </div>
                          {item.badge && (
                            <span className={cn(
                              "text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-tighter",
                              item.badge === 'NUEVO' ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                            )}>
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      )
                    })}
                  </div>
                ))}
              </nav>
              <div className="space-y-1 border-t pt-4 mt-auto">
                <button
                  onClick={async () => {
                    try {
                      await fetch('/api/auth/logout', { method: 'POST' })
                      window.location.href = '/login'
                    } catch (error) {
                      console.error('Error al cerrar sesión', error)
                    }
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  <FiLogOut className="h-5 w-5" />
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        <h2 className="text-lg font-semibold text-gray-800 hidden lg:block">
          Bienvenido, {usuario?.nombreCompleto?.split(' ')[0] || 'Usuario'}
        </h2>

        {(pathname.startsWith('/erp') || pathname.startsWith('/crm')) && (usuario as any)?.empresaFase && (
          <div className="ml-3 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider animate-in fade-in zoom-in duration-500"
            style={{
              backgroundColor: (usuario as any).empresaFase === 'PRODUCCIÓN' ? '#fee2e2' : '#e0f2fe',
              color: (usuario as any).empresaFase === 'PRODUCCIÓN' ? '#991b1b' : '#075985',
              border: `1px solid ${(usuario as any).empresaFase === 'PRODUCCIÓN' ? '#fecaca' : '#bae6fd'}`
            }}
          >
            Estado: {(usuario as any).empresaFase}
          </div>
        )}

        {/* Site Selector - Solo mostrar en rutas de hosting */}
        {!usuario?.isAdmin && usuario?.hostingAccounts && usuario.hostingAccounts.length > 0 && pathname.startsWith('/hosting') && (
          <div className="flex items-center gap-2 ml-2 lg:ml-4 flex-1 max-w-[320px]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="w-full justify-between gap-3 border-blue-100 bg-blue-50/50 hover:bg-blue-100/50 hover:border-blue-200 transition-all px-3 h-10 shadow-sm group">
                  <div className="flex items-center gap-2 truncate text-left">
                    <div className="flex flex-col truncate">
                      <span className="text-[9px] font-bold text-blue-500 uppercase tracking-widest leading-none mb-0.5">Sitio Activo</span>
                      <span className="truncate text-sm font-semibold text-slate-700 tracking-tight">
                        {selectedAccount?.dominio?.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      </span>
                    </div>
                  </div>
                  <FiMenu className="h-4 w-4 shrink-0 text-blue-400 group-hover:text-blue-600 transition-colors" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[300px] p-2 shadow-2xl border-blue-50/50">
                <DropdownMenuLabel className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 py-2">
                  Cambiar Sitio / Hosting
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="my-1 bg-slate-50" />
                <div className="max-h-[300px] overflow-y-auto scrollbar-thin">
                  {usuario.hostingAccounts.map((acc: any) => (
                    <DropdownMenuItem 
                      key={acc.id} 
                      className={cn(
                        "flex items-center gap-3 cursor-pointer py-3 px-3 rounded-xl transition-all mb-1",
                        selectedAccount?.id === acc.id 
                          ? "bg-blue-600 text-white shadow-md shadow-blue-100 hover:bg-blue-700" 
                          : "hover:bg-slate-50 text-slate-600"
                      )}
                      onClick={() => {
                        setSelectedAccount(acc)
                        localStorage.setItem('selected_hosting_account', acc.id.toString())
                        window.dispatchEvent(new Event('storage'))
                        window.location.reload()
                      }}
                    >
                      <div className={cn(
                        "h-8 w-8 rounded-lg flex items-center justify-center shrink-0",
                        selectedAccount?.id === acc.id ? "bg-white/20" : "bg-slate-100 text-slate-400"
                      )}>
                        <FiActivity className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col truncate">
                        <span className="font-bold text-sm tracking-tight truncate">
                          {acc.dominio?.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </span>
                        <span className={cn(
                          "text-[10px] font-mono uppercase tracking-tighter",
                          selectedAccount?.id === acc.id ? "text-blue-100" : "text-slate-400"
                        )}>
                          {acc.folderName || 'ROOT'}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <div className="flex items-center gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="relative rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 outline-none transition-colors">
              <FiBell className="h-5 w-5" />
              {countBadge > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white shadow-sm ring-2 ring-white animate-in zoom-in duration-300">
                    {countBadge}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 sm:w-96 shadow-xl border-gray-200">
            <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50/50">
                <span className="font-semibold text-sm">Notificaciones</span>
                {countBadge > 0 && (
                    <span className="text-xs text-muted-foreground bg-white px-2 py-0.5 rounded-full border shadow-sm">
                        {countBadge} nuevas
                    </span>
                )}
            </div>
            <div className="max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
              {notificaciones.alertas.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
                  <FiBell className="h-8 w-8 text-gray-200" />
                  <p>Al día con todo</p>
                </div>
              ) : (
                notificaciones.alertas.map((alerta: any) => {
                  const isUnread = !alerta.isRead
                  return (
                    <DropdownMenuItem 
                        key={alerta.id} 
                        className={cn(
                            "cursor-pointer focus:bg-accent border-b last:border-0 rounded-none p-0",
                            isUnread ? "bg-blue-50/60" : "bg-white opacity-80"
                        )}
                        asChild
                        onClick={() => marcarComoLeida(alerta.id)}
                    >
                      <div className="flex flex-col items-start gap-1 p-4 w-full transition-colors hover:bg-gray-100/80">
                        <div className="flex items-start justify-between w-full gap-2">
                          <div className="flex items-center gap-2">
                            <span className={cn(
                                "flex h-2 w-2 rounded-full shrink-0 mt-0.5", 
                                alerta.tipo === 'CRITICAL' ? 'bg-red-500' : 
                                alerta.categoria === 'Pago' ? 'bg-green-500' : 'bg-amber-500',
                                isUnread && "ring-2 ring-blue-100 ring-offset-1"
                            )} />
                            <span className={cn("text-sm leading-none", isUnread ? "font-semibold text-gray-900" : "font-medium text-gray-600")}>
                                {alerta.titulo}
                            </span>
                          </div>
                          {isUnread && (
                            <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" title="No leído"></span>
                          )}
                        </div>
                        <p className={cn("text-xs pl-4 line-clamp-2", isUnread ? "text-gray-600" : "text-gray-400")}>
                          {alerta.mensaje}
                        </p>
                        <div className="flex items-center justify-between w-full mt-1 pl-4">
                            <span className="text-[10px] text-gray-400">
                                {alerta.categoria}
                            </span>
                            {alerta.createdAt && (
                                <span className="text-[10px] text-gray-300">
                                    {new Date(alerta.createdAt).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                      </div>
                    </DropdownMenuItem>
                )
                })
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        
        <div className="flex items-center gap-3 border-l pl-4">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-medium text-gray-900">{usuario?.nombreCompleto}</p>
            <p className="text-xs text-gray-500">{usuario?.email}</p>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                <FiUser className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer" onClick={async () => {
                try {
                  await fetch('/api/auth/logout', { method: 'POST' })
                  window.location.href = '/login'
                } catch (error) {
                  console.error('Error al cerrar sesión', error)
                }
              }}>
                <FiLogOut className="mr-2 h-4 w-4" />
                <span>Cerrar Sesión</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
